import { gbp } from "@/lib/format";
import { ResponsiveContainer, BarChart, Bar, XAxis, Cell } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";

const CAT_COLORS = ["#00C896", "#34d399", "#0ea5e9", "#f59e0b", "#f43f5e", "#a78bfa", "#64748b"];

export default function CashFlowSection({ data }) {
  const { month_in = 0, month_out = 0, category_breakdown = [], income_breakdown = [] } = data || {};
  const chartData = [
    { name: "Money in", value: month_in, fill: "#00C896" },
    { name: "Money out", value: month_out, fill: "#f43f5e" },
  ];
  const maxCat = Math.max(1, ...category_breakdown.map((c) => c.amount));

  return (
    <div className="mm-glow-card p-6 h-full" data-testid="cashflow-section">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#00C896]">Cash flow</p>
          <h3 className="font-display text-lg font-semibold mt-1">This month</h3>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-[#00C896]/20 bg-[#00C896]/5 p-3">
          <div className="flex items-center gap-1.5 text-xs text-[#4DE3BF]"><TrendingUp className="h-3.5 w-3.5" /> Money in</div>
          <p data-testid="money-in" className="font-num text-xl font-bold text-[#00C896] mt-1">{gbp(month_in)}</p>
        </div>
        <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-3">
          <div className="flex items-center gap-1.5 text-xs text-rose-300"><TrendingDown className="h-3.5 w-3.5" /> Money out</div>
          <p data-testid="money-out" className="font-num text-xl font-bold text-rose-400 mt-1">{gbp(month_out)}</p>
        </div>
      </div>

      <div className="mt-4 h-24">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
            <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
            <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={90}>
              {chartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2">
        <p className="text-xs text-slate-500 mb-2">Where it went</p>
        <div className="space-y-2">
          {category_breakdown.slice(0, 5).map((c, i) => (
            <div key={c.category} data-testid={`cashflow-cat-${c.category}`}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-300">{c.category}</span>
                <span className="font-num text-slate-400">{gbp(c.amount)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(c.amount / maxCat) * 100}%`, background: CAT_COLORS[i % CAT_COLORS.length] }} />
              </div>
            </div>
          ))}
          {category_breakdown.length === 0 && <p className="text-sm text-slate-500">No spending recorded this month.</p>}
        </div>
      </div>
    </div>
  );
}
